/**
 * Shared plumbing for privacy features: cancelling future appointments without fees (account closure, client erasure),
 * hard-deleting a whole business, and the written retention policy. Not part of the UI contract.
 */
import { ENTITY_NAMES, type Appointment, type AppointmentStatus, type EntityName, type Id } from '../types';
import { toMs } from '../time';
import type { Deps } from './context';

/** Days between "delete my account" and the hard delete. During them the account is closed to clients and can be restored by signing in. */
export const DELETION_GRACE_DAYS = 14;

/**
 * What a hard delete removes and what it keeps (Israeli Privacy Protection Law amendment 13, Apple 5.1.1(v)).
 * `RETAIN_TAX_RECORDS` is the documented switch: Planella's own subscription invoices to the owner are tax books (Israeli
 * bookkeeping law, 7 years [accountant to confirm]); they live with the billing provider on the server, never in the business data
 * this module deletes, so the demo has nothing to keep. Client-facing invoices do not exist in this build (the owner issues them elsewhere).
 */
export const RETAIN_TAX_RECORDS = true;
export const DELETION_POLICY = {
  graceDays: DELETION_GRACE_DAYS,
  /** Every table is emptied for the business, including clients, notes, photos (inside the business row), messages and the consent ledger. */
  purged: ENTITY_NAMES,
  /** Kept after a hard delete, outside the business data. */
  retained: RETAIN_TAX_RECORDS ? ['billing_invoices_server_side'] as const : [] as const,
  /**
   * Production note: the consent ledger is append-only server side. On account deletion the server keeps only a de-identified proof
   * (hashed phone, purpose, timestamp, wording version) when the lawyer confirms the Communications Law s.30A evidence duty; the demo purges it.
   */
  consentLedger: 'purged' as const,
} as const;

export type PrivacyCancelKind = 'owner_closed' | 'client_erased';

const LIVE: readonly AppointmentStatus[] = ['awaiting_deposit', 'pending', 'confirmed'];

/** Future appointments that are still alive (held, pending or confirmed). */
export const isFutureLive = (a: Appointment, nowMs: number): boolean => LIVE.includes(a.status) && toMs(a.end) > nowMs;

/**
 * Cancels one appointment with no fee and a full deposit refund; queued reminders are cancelled. Sends nothing:
 * callers decide who is told. Returns the stored row, or undefined when it was no longer live.
 */
export async function cancelWithoutFee(d: Deps, appointmentId: Id, kind: PrivacyCancelKind): Promise<Appointment | undefined> {
  const t = d.clock.nowIso();
  const before = await d.storage.repo('appointments').get(appointmentId);
  if (!before) return undefined;
  const next = await d.storage.transact(['appointments'], async (tx) => {
    const cur = await tx.get('appointments', appointmentId);
    if (!cur || !LIVE.includes(cur.status)) return undefined;
    const { holdExpiresAt: _h, ...rest } = cur;
    const row: Appointment = {
      ...rest, status: kind === 'owner_closed' ? 'cancelled_owner' : 'cancelled_client', cancelledAt: t, cancelReason: kind === 'owner_closed' ? 'owner' : 'client', updatedAt: t,
    };
    await tx.put('appointments', row);
    return row;
  });
  if (!next) return undefined;
  let result = next;
  if (before.paymentId) {
    if (before.status === 'awaiting_deposit') await d.payments.complete(before.paymentId, 'failure');
    else {
      const paid = await d.payments.getStatus(before.paymentId);
      if (paid && paid.status === 'paid') {
        const r = await d.payments.refund(before.paymentId);
        if (r.ok) { result = { ...next, depositStatus: 'refunded', updatedAt: d.clock.nowIso() }; await d.storage.repo('appointments').put(result); }
      }
    }
  }
  for (const m of await d.storage.repo('messages').list({ where: { appointmentId }, filter: (x) => x.status === 'queued' })) await d.messaging.cancel(m.id);
  return result;
}

/** Hard-deletes every row of a business in one transaction (all 14 tables). Returns the number of rows removed per table. */
export async function purgeBusiness(d: Deps, businessId: Id): Promise<Record<string, number>> {
  const counts: Record<string, number> = {};
  await d.storage.transact(ENTITY_NAMES, async (tx) => {
    for (const name of ENTITY_NAMES as readonly EntityName[]) {
      const rows = name === 'businesses' ? [await tx.get('businesses', businessId)].filter(Boolean)
        : name === 'settings' ? [await tx.get('settings', businessId)].filter(Boolean)
          : await tx.list(name, { where: { businessId } as never });
      counts[name] = rows.length;
      for (const r of rows) await tx.delete(name, (r as { id: Id }).id);
    }
  });
  return counts;
}
